/**
 * The automation page's save bar: pinned to the bottom of the editor, where
 * the attorney's eye already is after typing, so Save never needs a scroll
 * back to the top bar. It appears the moment anything changes ("Unsaved
 * Changes", and which parts), offers Discard and a primary Save (⌘S does the
 * same), shows "Saving…" while the request runs and a quiet "Saved" for a
 * moment after, then gets out of the way.
 */
import { Check } from "lucide-react";
import { useEffect, useRef, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { Kbd } from "@/components/ui/kbd";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";

/** What each part of the draft is called on the page. */
const PART_LABEL: Record<string, string> = {
  content: "Name and Instructions",
  authority: "Permissions",
  triggers: "Schedule",
  visas: "Visas"
};

/** The changed parts, as the page names them ("Name and Instructions, Permissions"). */
export const changedPartsLabel = (changed: readonly string[]): string =>
  changed.map((k) => PART_LABEL[k] ?? k).join(", ");

/** How long "Saved" stays after a save lands. */
export const SAVED_FLASH_MS = 2200;

export const SAVE_SHORTCUT =
  typeof navigator !== "undefined" && /Mac|iP(hone|ad)/.test(navigator.platform)
    ? "⌘S"
    : "Ctrl+S";

type Props = {
  /** The draft differs from what is saved. */
  dirty: boolean;
  saving: boolean;
  /** Which parts of the draft changed (`content`, `authority`, …). */
  changed: readonly string[];
  onSave: () => void;
  onDiscard: () => void;
};

export const SkillSaveBar: FC<Props> = ({
  dirty,
  saving,
  changed,
  onSave,
  onDiscard
}) => {
  // "Saved": the request that was running finished and nothing is left unsaved.
  const [saved, setSaved] = useState(false);
  const wasSaving = useRef(false);
  useEffect(() => {
    if (saving) {
      wasSaving.current = true;
      setSaved(false);
      return undefined;
    }
    if (!wasSaving.current) return undefined;
    wasSaving.current = false;
    if (dirty) return undefined; // the save failed or more was typed
    setSaved(true);
    const timer = window.setTimeout(() => setSaved(false), SAVED_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [saving, dirty]);
  useEffect(() => {
    if (dirty) setSaved(false);
  }, [dirty]);

  const state = saving ? "saving" : dirty ? "dirty" : saved ? "saved" : null;
  if (!state) return null;

  return (
    <div
      className="shrink-0 border-t border-line-2 bg-chrome px-6 py-2"
      role="status"
      aria-live="polite"
      data-testid="skill-save-bar"
      data-state={state}
    >
      <div className="mx-auto flex w-full max-w-180 items-center gap-3">
        {state === "saved" ? (
          <span
            className="flex items-center gap-1.5 text-base text-success"
            data-testid="skill-save-bar-saved"
          >
            <Check className="size-3.5" /> Saved
          </span>
        ) : (
          <>
            <StatusBadge tone="warn" dot data-testid="skill-save-bar-status">
              Unsaved Changes
            </StatusBadge>
            {changed.length ? (
              <span
                className="min-w-0 truncate text-sm text-fg-3"
                data-testid="skill-save-bar-parts"
              >
                {changedPartsLabel(changed)}
              </span>
            ) : null}
          </>
        )}
        <span className="flex-1" />
        {state === "saved" ? null : (
          <>
            <Button
              variant="ghost"
              disabled={saving}
              onClick={onDiscard}
              data-testid="skill-save-bar-discard"
            >
              Discard
            </Button>
            <Button
              variant="primary"
              disabled={saving}
              onClick={onSave}
              data-testid="skill-save-bar-save"
            >
              {saving ? <Spinner className="size-3.5" /> : null}
              {saving ? "Saving…" : "Save"}
              {saving ? null : (
                <Kbd className="bg-transparent text-primary-foreground/70">
                  {SAVE_SHORTCUT}
                </Kbd>
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
